import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Globe, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  BOOKING_CUSTOM_HOST_TXT_PREFIX,
  normalizeOnlineBookingPortalHostname,
} from '../../lib/onlineBookingUrls';
import { isCustomHostnameTakenGlobally } from '../../lib/customHostnameConflicts';
import { useSupabase } from '../../contexts/SupabaseContext';

const DEFAULT_BOOKING_CNAME_TARGET =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_INTAKE_CUSTOM_HOST_CNAME_TARGET) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PROPOSAL_CUSTOM_HOST_CNAME_TARGET) ||
  'cname.vercel-dns.com';

type PortalHostState = {
  custom_hostname?: string | null;
  custom_host_verified_at?: string | null;
  custom_host_verification_token?: string | null;
};

export interface BrandedOnlineBookingPortalHostSectionProps {
  businessId: string;
  portalId: string;
  onUpdated?: () => void;
}

/**
 * Per-portal optional custom hostname for public /{slug}/book… URLs (online booking marketing).
 */
export default function BrandedOnlineBookingPortalHostSection({
  businessId,
  portalId,
  onUpdated,
}: BrandedOnlineBookingPortalHostSectionProps) {
  const supabase = useSupabase();
  const [hostState, setHostState] = useState<PortalHostState | null>(null);
  const [hostInput, setHostInput] = useState('');
  const [hostLoading, setHostLoading] = useState(false);
  const [hostSaving, setHostSaving] = useState(false);
  const [hostVerifying, setHostVerifying] = useState(false);
  const [hostRemoving, setHostRemoving] = useState(false);
  const step2Ref = useRef<HTMLDivElement>(null);
  const hadPendingTokenRef = useRef(false);

  const loadPortalHost = useCallback(async () => {
    if (!portalId || !supabase) return;
    setHostLoading(true);
    try {
      const { data, error } = await supabase
        .from('online_booking_portals')
        .select('custom_hostname, custom_host_verified_at, custom_host_verification_token')
        .eq('id', portalId)
        .eq('business_id', businessId)
        .maybeSingle();
      if (error) throw error;
      const row = data as PortalHostState | null;
      setHostState(row);
      setHostInput(row?.custom_hostname || '');
    } catch (e) {
      console.error(e);
      toast.error('Failed to load booking domain settings');
    } finally {
      setHostLoading(false);
    }
  }, [portalId, businessId, supabase]);

  useEffect(() => {
    void loadPortalHost();
  }, [loadPortalHost]);

  const fqdnForTxt = (hostname: string) =>
    `${BOOKING_CUSTOM_HOST_TXT_PREFIX}.${normalizeOnlineBookingPortalHostname(hostname)}`;

  const copyText = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleSavePending = async () => {
    if (!supabase) return;
    const normalized = normalizeOnlineBookingPortalHostname(hostInput);
    if (!normalized) {
      toast.error('Enter a hostname (e.g. book.yourbusiness.com)');
      return;
    }
    setHostSaving(true);
    try {
      const conflict = await isCustomHostnameTakenGlobally(supabase, normalized, {
        excludeOnlineBookingPortalId: portalId,
      });
      if (conflict.taken) {
        toast.error(conflict.reason || 'That hostname is not available');
        return;
      }
      const token = crypto.randomUUID();
      const { error } = await supabase
        .from('online_booking_portals')
        .update({
          custom_hostname: normalized,
          custom_host_verification_token: token,
          custom_host_verified_at: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', portalId)
        .eq('business_id', businessId);
      if (error) throw error;
      await loadPortalHost();
      onUpdated?.();
      toast.success('Next: add the TXT record (step 2), then click Verify DNS.');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setHostSaving(false);
    }
  };

  const handleVerify = async () => {
    if (!supabase) return;
    setHostVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-online-booking-portal-custom-host', {
        body: { portalId },
      });
      if (error) throw new Error(error.message);
      if (data?.ok) {
        toast.success('Domain verified. This booking page link can use your hostname.');
        if (data.vercelProvisioning === 'skipped' && data.vercelDetail) {
          toast.message(data.vercelDetail, { duration: 10000 });
        }
        await loadPortalHost();
        onUpdated?.();
      } else {
        toast.error(data?.message || 'Verification failed');
      }
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Verification failed');
    } finally {
      setHostVerifying(false);
    }
  };

  const handleStartOverPending = async () => {
    if (
      !window.confirm(
        'Discard this setup? DNS records you added can stay or be removed; you can enter the hostname again from step 1.',
      )
    ) {
      return;
    }
    if (!supabase) return;
    setHostRemoving(true);
    try {
      const { error } = await supabase
        .from('online_booking_portals')
        .update({
          custom_hostname: null,
          custom_host_verified_at: null,
          custom_host_verification_token: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', portalId)
        .eq('business_id', businessId);
      if (error) throw error;
      setHostInput('');
      await loadPortalHost();
      onUpdated?.();
      toast.success('Setup cleared — enter your hostname again when ready.');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not reset');
    } finally {
      setHostRemoving(false);
    }
  };

  const handleRemoveVerified = async () => {
    if (!window.confirm('Remove your custom domain? Links for this booking page will use the default app URL.')) {
      return;
    }
    if (!supabase) return;
    setHostRemoving(true);
    try {
      const { error } = await supabase
        .from('online_booking_portals')
        .update({
          custom_hostname: null,
          custom_host_verified_at: null,
          custom_host_verification_token: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', portalId)
        .eq('business_id', businessId);
      if (error) throw error;
      setHostInput('');
      await loadPortalHost();
      onUpdated?.();
      toast.success('Custom domain removed');
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not remove');
    } finally {
      setHostRemoving(false);
    }
  };

  const pendingToken = hostState?.custom_host_verification_token;
  const verified = Boolean(hostState?.custom_host_verified_at);
  const hostnameForDns = pendingToken
    ? normalizeOnlineBookingPortalHostname(String(hostState?.custom_hostname ?? ''))
    : normalizeOnlineBookingPortalHostname(hostInput);
  const txtFqdn = hostnameForDns ? fqdnForTxt(hostnameForDns) : '';
  const txtValue = pendingToken ? `nexus-booking-verify=${pendingToken}` : '';
  const showStep2 = Boolean(pendingToken) && !verified;

  useEffect(() => {
    const nowPending = Boolean(pendingToken) && !verified;
    if (nowPending && !hadPendingTokenRef.current && step2Ref.current) {
      step2Ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    hadPendingTokenRef.current = nowPending;
  }, [pendingToken, verified]);

  return (
    <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/40 px-3 py-3 space-y-3">
      <div className="flex items-start gap-2.5">
        <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <Globe className="h-4 w-4 text-emerald-700" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-xs font-semibold text-gray-900">Branded link for this booking page (optional)</h4>
          <p className="text-[11px] text-gray-600 mt-0.5 leading-snug">
            Same DNS flow as proposal / intake: your subdomain, then TXT verification. Each booking page can have its
            own hostname.
          </p>
        </div>
      </div>

      {hostLoading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Hostname</label>
            <input
              type="text"
              value={hostInput}
              onChange={(e) => setHostInput(e.target.value)}
              placeholder="book.yourbusiness.com"
              disabled={verified || Boolean(pendingToken)}
              title={
                pendingToken && !verified
                  ? 'Finish verification or start over before changing the hostname'
                  : undefined
              }
              className="w-full px-2.5 py-2 rounded-lg border border-gray-200 text-xs disabled:bg-gray-50 disabled:text-gray-600"
            />
            {verified && (
              <p className="text-[11px] text-emerald-800 mt-1 flex items-center gap-1">
                <Check className="h-3 w-3" />
                Verified{' '}
                {hostState?.custom_host_verified_at
                  ? format(new Date(hostState.custom_host_verified_at), 'MMM d, yyyy h:mm a')
                  : ''}
              </p>
            )}
          </div>

          {!verified && (
            <>
              {!showStep2 && (
                <div className="rounded-lg bg-white/80 border border-emerald-100/80 p-2.5 text-[11px] text-gray-700 space-y-2">
                  <p className="font-semibold text-gray-900">Step 1 — Point your subdomain here</p>
                  <p>
                    Add a <strong>CNAME</strong> at your DNS provider for{' '}
                    <span className="font-medium">{hostnameForDns || 'your host'}</span> targeting:
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-[10px] bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 break-all">
                      {DEFAULT_BOOKING_CNAME_TARGET}
                    </code>
                    <button
                      type="button"
                      onClick={() => void copyText('CNAME target', String(DEFAULT_BOOKING_CNAME_TARGET))}
                      className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-[11px]"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={hostSaving}
                    onClick={() => void handleSavePending()}
                    className="w-full sm:w-auto px-3 py-2 rounded-lg text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                  >
                    {hostSaving ? 'Saving…' : 'Save & continue to step 2'}
                  </button>
                </div>
              )}

              {showStep2 && (
                <div ref={step2Ref} className="space-y-2">
                  <div className="rounded-lg bg-white/80 border border-gray-200 px-2 py-1.5 text-[10px] text-gray-600">
                    <span className="font-semibold text-gray-800">Step 1</span> — Keep your CNAME pointing to{' '}
                    <code className="bg-gray-50 px-1 rounded">{DEFAULT_BOOKING_CNAME_TARGET}</code>.
                  </div>

                  <div className="rounded-lg bg-amber-50/90 border border-amber-100 p-2.5 text-[11px] text-amber-950 space-y-2">
                    <p className="font-semibold text-gray-900">Step 2 — Prove you own the domain</p>
                    <p>Add this <strong>TXT</strong> record:</p>
                    {txtFqdn && txtValue ? (
                      <div className="space-y-1.5">
                        <div>
                          <p className="text-[9px] font-medium uppercase tracking-wide text-amber-900/70 mb-0.5">
                            Name / host
                          </p>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <span className="text-[10px] font-mono break-all leading-relaxed">{txtFqdn}</span>
                            <button
                              type="button"
                              onClick={() => void copyText('TXT name', txtFqdn)}
                              className="text-amber-900 font-semibold shrink-0 inline-flex items-center gap-1 text-[11px]"
                            >
                              <Copy className="h-3 w-3" /> Copy
                            </button>
                          </div>
                        </div>
                        <div>
                          <p className="text-[9px] font-medium uppercase tracking-wide text-amber-900/70 mb-0.5">
                            Value
                          </p>
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <span className="text-[10px] font-mono break-all leading-relaxed">{txtValue}</span>
                            <button
                              type="button"
                              onClick={() => void copyText('TXT value', txtValue)}
                              className="text-amber-900 font-semibold shrink-0 inline-flex items-center gap-1 text-[11px]"
                            >
                              <Copy className="h-3 w-3" /> Copy
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      disabled={hostVerifying}
                      onClick={() => void handleVerify()}
                      className="w-full sm:w-auto px-3 py-2 rounded-lg text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                    >
                      {hostVerifying ? 'Checking DNS…' : 'Verify DNS'}
                    </button>
                    <button
                      type="button"
                      disabled={hostRemoving || hostVerifying}
                      onClick={() => void handleStartOverPending()}
                      className="block text-[10px] font-medium text-gray-500 hover:text-gray-800 underline-offset-2 hover:underline"
                    >
                      Start over (change hostname)
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {verified && hostState?.custom_hostname && (
            <div className="flex flex-wrap gap-2 pt-0.5">
              <button
                type="button"
                disabled={hostRemoving}
                onClick={() => void handleRemoveVerified()}
                className="px-3 py-2 rounded-lg text-xs font-semibold text-red-700 bg-red-50 border border-red-100 hover:bg-red-100 disabled:opacity-40"
              >
                {hostRemoving ? 'Removing…' : 'Remove custom domain'}
              </button>
            </div>
          )}

          {!verified && (
            <p className="text-[10px] text-gray-500">
              This hostname must not already be used for intake, proposals, feedback, or another booking page.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
